import React from "react";
import { Tooltip } from "antd";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { fmtH, fmtMins } from "../../components/classic/micro";
import { ToolIcon, brandColorFor, glyphColor } from "../../components/classic/panels";

/** Theme inks — blue / violet / amber. Never green for "active". */
export function inks(t) {
  return {
    blue: t.c.primary,
    blueD: t.c.primaryHover,
    gold: t.isDark ? "#F0B24A" : "#D97706", // 2026-08-30: was #eda100 — read as washed yellow on the light canvas
    // Time. Deliberately not another blue: scope (members) already owns the
    // page accent, and two blues side by side in the toolbar would blur the
    // two filters into one.
    teal: t.isDark ? "#3FC9B0" : "#0E9F8A",
    violet: t.c.ai,
    pink: "#e87ba4",
    mute: t.c.border,
    ink: t.c.text,
    dim: t.c.textTertiary,
  };
}

/** Cooler, less-white paper for this dashboard only. Dark theme stays on tokens. */
export function paper(t) {
  if (t.isDark) {
    return { canvas: t.c.bg, card: t.c.surface, well: t.c.surfaceMuted };
  }
  return { canvas: "#D4DBE6", card: "#E8EDF5", well: "#DCE3EE" };
}

export function toolColors(t) {
  const k = inks(t);
  return [k.blue, k.violet, k.blueD, k.pink, k.gold, t.c.textTertiary];
}

export function smooth(pts, closeY) {
  if (!pts.length) return "";
  let d = `M${pts[0][0]},${pts[0][1]}`;
  for (let i = 0; i < pts.length - 1; i += 1) {
    const [x0, y0] = pts[i];
    const [x1, y1] = pts[i + 1];
    const mx = (x0 + x1) / 2;
    d += ` C${mx},${y0} ${mx},${y1} ${x1},${y1}`;
  }
  if (closeY != null) d += ` L${pts[pts.length - 1][0]},${closeY} L${pts[0][0]},${closeY} Z`;
  return d;
}

export function dayLabel(dateLocal) {
  if (!dateLocal) return "";
  const d = new Date(`${dateLocal}T12:00:00`);
  return ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][d.getDay()] || dateLocal.slice(5);
}

export function hhLabel(h) {
  const hr = h % 12 || 12;
  return `${hr} ${h < 12 ? "AM" : "PM"}`;
}

/** hourCurve is total active minutes per hour. Average people ≈ mins / 60 / days. */
export function hourPeople(overview) {
  const curve = overview?.hourCurve;
  const days = overview?.range?.days || 1;
  if (!Array.isArray(curve) || !curve.some((v) => v > 0)) return null;
  return Array.from({ length: 24 }, (_, i) => (curve[i] || 0) / 60 / Math.max(1, days));
}

export function isSingleDay(overview) {
  if (!overview) return false;
  if (overview.range?.days === 1) return true;
  if (overview.range?.start && overview.range.start === overview.range.end) return true;
  return (overview.daySeries || []).length === 1;
}

export function prettyDay(iso) {
  if (!iso) return "";
  const d = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
}

export function Gauge({ t, activePct }) {
  const k = inks(t);
  // viewBox starts at y −12: the 18px arc stroke overhangs the path apex
  // (y=0) by 9px, and a 0-origin viewport was shaving it flat.
  const R = 112;
  const circ = Math.PI * R;
  const pct = Math.max(0, Math.min(100, Number(activePct) || 0)) / 100;
  const rest = 1 - pct;
  const both = pct > 0.01 && rest > 0.01;
  const gap = both ? 16 : 0;
  const usable = circ - gap;
  const act = both ? pct * usable : (pct > 0 ? circ : 0);
  const idle = both ? rest * usable : (rest > 0 ? circ : 0);
  return (
    <svg viewBox="0 -12 268 145" width="100%" height="132">
      <defs>
        <linearGradient id="tgwGA" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#789FE8" /><stop offset="100%" stopColor={k.blueD} />
        </linearGradient>
        <linearGradient id="tgwGI" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#F4D078" /><stop offset="100%" stopColor={k.gold} />
        </linearGradient>
      </defs>
      <path d="M22 112 A112 112 0 0 1 246 112" fill="none" stroke={t.c.surfaceMuted} strokeWidth="18" strokeLinecap="round" />
      {idle > 0 && (
        <path d="M22 112 A112 112 0 0 1 246 112" fill="none" stroke="url(#tgwGI)" strokeWidth="18" strokeLinecap="round"
          strokeDasharray={`${idle} ${circ}`} strokeDashoffset={-(circ - idle)} />
      )}
      {act > 0 && (
        <path d="M22 112 A112 112 0 0 1 246 112" fill="none" stroke="url(#tgwGA)" strokeWidth="18" strokeLinecap="round"
          strokeDasharray={`${act} ${circ}`} strokeDashoffset={act} className="tgw-gact" />
      )}
      <text x="134" y="92" textAnchor="middle" fontSize="28" fontWeight="720" fill={k.ink}>{activePct}%</text>
      <text x="134" y="110" textAnchor="middle" fontSize="10" fontWeight="800" letterSpacing="1.2" fill={k.dim}>ACTIVE</text>
    </svg>
  );
}

/**
 * Hours-by-day, as HTML bars that fill the panel.
 *
 * The SVG version drew into a fixed 680px canvas centred in the card —
 * dead margins, a lake of space below, and blob-shaped pills on small
 * days. HTML flex columns stretch to the card's real size; each day is a
 * stacked active/idle bar with its total on top, a grey tick marks the
 * same day last period (clearer than the old unlabeled dashed curve),
 * and clicking a day opens its drill.
 */
export function WeekStream({ t, series, onDay }) {
  const k = inks(t);
  const [hov, setHov] = React.useState(null);
  const DAYS = series || [];
  if (!DAYS.length) return null;
  const max = Math.max(
    ...DAYS.map((d) => (d.activeMinutes || 0) + (d.idleMinutes || 0)),
    ...DAYS.map((d) => d.prevTotalMinutes || 0),
    60
  );
  const anyPrev = DAYS.some((d) => (d.prevTotalMinutes || 0) > 0);

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 230, paddingTop: 8 }}>
      <div
        style={{ flex: 1, display: "flex", alignItems: "stretch", gap: "1%", minHeight: 160 }}
        onMouseLeave={() => setHov(null)}
      >
        {DAYS.map((d, i) => {
          const a = d.activeMinutes || 0;
          const idle = d.idleMinutes || 0;
          const tot = a + idle;
          const prevM = d.prevTotalMinutes || 0;
          const pct = tot > 0 ? Math.max(4, (tot / max) * 100) : 0;
          const prevPct = prevM > 0 ? (prevM / max) * 100 : null;
          return (
            <div
              key={d.dateLocal || i}
              role={onDay ? "button" : undefined}
              tabIndex={onDay ? 0 : undefined}
              title={`${prettyDay(d.dateLocal) || d.dateLocal} · ${fmtMins(a)} active · ${fmtMins(idle)} idle${prevM ? ` · last period ${fmtH(prevM)}` : ""}`}
              onMouseEnter={() => setHov(i)}
              onClick={onDay ? () => onDay(d) : undefined}
              onKeyDown={onDay ? (e) => { if (e.key === "Enter") onDay(d); } : undefined}
              style={{
                flex: 1, minWidth: 0, display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "flex-end", position: "relative",
                cursor: onDay ? "pointer" : "default", outline: "none",
              }}
            >
              {tot > 0 ? (
                <span style={{
                  fontSize: 12, fontWeight: 720, color: k.ink, marginBottom: 6,
                  fontVariantNumeric: "tabular-nums",
                  opacity: hov == null || hov === i ? 1 : 0.45,
                  transition: "opacity .15s ease",
                }}>{fmtH(tot)}</span>
              ) : null}
              <span style={{
                position: "relative", width: "min(46%, 30px)",
                height: `${pct}%`, minHeight: tot > 0 ? 8 : 3, maxHeight: "82%",
                display: "flex", flexDirection: "column", borderRadius: 99, overflow: "hidden",
                opacity: hov == null || hov === i ? 1 : 0.4,
                transition: "opacity .15s ease",
              }} className="tgw-growY">
                {tot > 0 ? (
                  <>
                    <i style={{ display: "block", height: `${(idle / Math.max(tot, 1)) * 100}%`, background: `linear-gradient(180deg, #F6D48A, ${k.gold})` }} />
                    <i style={{ display: "block", flex: 1, background: `linear-gradient(180deg, #789FE8, ${k.blueD})` }} />
                  </>
                ) : (
                  <i style={{ display: "block", height: "100%", background: k.blue, opacity: 0.18 }} />
                )}
              </span>
              {prevPct != null ? (
                <span aria-hidden="true" style={{
                  position: "absolute", left: "50%", transform: "translateX(-50%)",
                  bottom: `${Math.min(prevPct, 82)}%`, width: "min(62%, 40px)", height: 2,
                  borderRadius: 2, background: t.c.borderStrong || t.c.border, opacity: 0.9,
                }} />
              ) : null}
            </div>
          );
        })}
      </div>
      <div style={{ display: "flex", gap: "1%", marginTop: 8, borderTop: `1px solid ${t.c.border}`, paddingTop: 7 }}>
        {DAYS.map((d, i) => (
          <span key={d.dateLocal || i} style={{
            flex: 1, minWidth: 0, textAlign: "center", fontSize: 11.5, color: k.dim, fontWeight: 600,
          }}>{dayLabel(d.dateLocal)}</span>
        ))}
      </div>
      <div style={{ display: "flex", gap: 16, marginTop: 8, fontSize: 11.5, color: t.c.textTertiary, flexWrap: "wrap" }}>
        <span><i style={{ display: "inline-block", width: 8, height: 8, borderRadius: 2, background: k.blue, marginRight: 5 }} />Active</span>
        <span><i style={{ display: "inline-block", width: 8, height: 8, borderRadius: 2, background: k.gold, marginRight: 5 }} />Idle</span>
        {anyPrev ? (
          <span><i style={{ display: "inline-block", width: 12, height: 2, borderRadius: 2, background: t.c.borderStrong || t.c.border, marginRight: 5, verticalAlign: "middle" }} />Same day, last period</span>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Hours-through-the-day, as HTML bars that fill the panel.
 *
 * The SVG version drew into a fixed 680×268 canvas centred in the card:
 * dead margins both sides, a lake of empty space below, min-height pills
 * that read as smudges on zero hours, and a bare "9a · 2.0" peak label.
 * HTML flex bars stretch to the card's real size, zero hours become thin
 * ticks, and the peak (or hovered hour) gets a labelled chip that says
 * what the number is.
 */
export function HourColumns({ t, hourPeople: hours }) {
  const k = inks(t);
  const [hov, setHov] = React.useState(null);
  const HOURS = hours || [];
  if (HOURS.length !== 24) return null;
  const mx = Math.max(...HOURS, 0.4);
  const peak = HOURS.indexOf(Math.max(...HOURS));
  const fmtVal = (v) => (Math.abs(v - Math.round(v)) < 0.05 ? String(Math.round(v)) : v.toFixed(1));
  const chipFor = hov != null ? hov : peak;
  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 220, paddingTop: 26 }}>
      <div style={{ flex: 1, display: "flex", alignItems: "flex-end", gap: "0.55%", minHeight: 150 }}
        onMouseLeave={() => setHov(null)}>
        {HOURS.map((v, h) => {
          const zero = v <= 0;
          const pct = Math.max(3, (v / mx) * 100);
          const isPeak = h === peak && v > 0;
          const isChip = h === chipFor && v > 0;
          return (
            <div
              key={h}
              title={`${hhLabel(h)} · ${fmtVal(v)} ${v === 1 ? "person" : "people"} active`}
              onMouseEnter={() => setHov(h)}
              style={{ flex: 1, minWidth: 0, height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end", position: "relative", cursor: "default" }}
            >
              {isChip ? (
                <span style={{
                  position: "absolute", bottom: `calc(${zero ? 0 : pct}% + 8px)`, left: "50%",
                  transform: "translateX(-50%)", whiteSpace: "nowrap", zIndex: 2,
                  fontSize: 11.5, fontWeight: 750, color: t.c.text,
                  background: paper(t).card, border: `1px solid ${t.c.border}`,
                  borderRadius: 999, padding: "2px 9px",
                  boxShadow: `0 4px 10px -4px ${t.c.shadowColor}`,
                }}>
                  {hhLabel(h)} · {fmtVal(v)} {v === 1 ? "person" : "people"}
                </span>
              ) : null}
              {/* Width is capped: at full panel width an uncapped flex bar
                  becomes an 80px blob and the hours melt together. */}
              {zero ? (
                <span style={{ height: 3, borderRadius: 2, background: k.blue, opacity: 0.18, width: "min(58%, 22px)" }} />
              ) : (
                <span
                  className="tgw-growY"
                  style={{
                    height: `${pct}%`, borderRadius: 99, minHeight: 6, width: "min(58%, 22px)",
                    background: isPeak ? `linear-gradient(180deg, #789FE8, ${k.blueD})` : k.blue,
                    opacity: hov === h ? 1 : isPeak ? 1 : 0.28 + (v / mx) * 0.62,
                    transition: "opacity .15s ease",
                    animationDelay: `${0.04 + h * 0.018}s`,
                  }}
                />
              )}
            </div>
          );
        })}
      </div>
      <div style={{ display: "flex", gap: "0.55%", marginTop: 8, borderTop: `1px solid ${t.c.border}`, paddingTop: 7 }}>
        {HOURS.map((_, h) => (
          <span key={h} style={{ flex: 1, minWidth: 0, textAlign: "center", fontSize: 10.5, color: k.dim, fontVariantNumeric: "tabular-nums" }}>
            {h % 3 === 0 ? hhLabel(h) : ""}
          </span>
        ))}
      </div>
    </div>
  );
}

export function IdleSplit({ t, activeMinutes, idleMinutes }) {
  const k = inks(t);
  const tot = (activeMinutes || 0) + (idleMinutes || 0);
  const idlePct = tot > 0 ? Math.round(((idleMinutes || 0) / tot) * 100) : 0;
  return (
    <div style={{ padding: "16px 0 8px" }}>
      <div style={{ display: "flex", height: 14, borderRadius: 99, overflow: "hidden", gap: 3 }}>
        <i className="tgw-growX" style={{ width: `${Math.max(0, 100 - idlePct)}%`, background: k.blue }} />
        <i className="tgw-growX" style={{ width: `${idlePct}%`, background: k.gold, animationDelay: ".12s" }} />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, fontSize: 11, color: k.dim }}>
        <span>{fmtMins(activeMinutes)} active</span>
        <span>{fmtMins(idleMinutes)} idle · {idlePct}%</span>
      </div>
    </div>
  );
}

export function ActiveWaves({ t, series, values }) {
  const k = inks(t);
  const vals = values || (series || []).map((d) => (d.activeMinutes || 0) + (d.idleMinutes || 0));
  const idle = values ? null : (series || []).map((d) => d.idleMinutes || 0);
  if (vals.length < 2) return null;
  const W = 240, H = 78;
  const mx = Math.max(...vals, 1);
  const toPts = (arr) => arr.map((v, i) => [i * (W / (arr.length - 1)), H - 8 - (v / mx) * (H - 16)]);
  const a = toPts(vals);
  const idlePts = idle && idle.length === vals.length ? toPts(idle) : null;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H}>
      <defs>
        <linearGradient id="tgwWa" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={k.blue} stopOpacity=".4" /><stop offset="100%" stopColor={k.blue} stopOpacity="0" />
        </linearGradient>
        <linearGradient id="tgwWi" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={k.gold} stopOpacity=".32" /><stop offset="100%" stopColor={k.gold} stopOpacity="0" />
        </linearGradient>
      </defs>
      {idlePts && <path d={smooth(idlePts, H)} fill="url(#tgwWi)" />}
      <path d={smooth(a, H)} fill="url(#tgwWa)" />
      <path d={smooth(a)} fill="none" stroke={k.blueD} strokeWidth="2.2" />
    </svg>
  );
}

export function IdleLolli({ t, series }) {
  const k = inks(t);
  const vals = (series || []).map((d) => d.idleMinutes || 0);
  if (!vals.length) return null;
  const mx = Math.max(...vals, 1);
  const W = 240, H = 74, p = 14;
  const worst = vals.indexOf(Math.max(...vals));
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H}>
      {vals.map((v, i) => {
        const x = vals.length === 1 ? W / 2 : p + i * ((W - p * 2) / (vals.length - 1));
        const y = H - 12 - (v / mx) * 50;
        const d = series[i];
        return (
          <g key={d.dateLocal || i} className="tgw-pop" style={{ animationDelay: `${0.18 + i * 0.04}s` }}>
            <title>{`${d.dateLocal} · ${fmtMins(v)} idle`}</title>
            <line x1={x} y1={H - 12} x2={x} y2={y} stroke="#F0D59A" strokeWidth="3.4" strokeLinecap="round" />
            <circle cx={x} cy={y} r={i === worst ? 6.5 : 5} fill={i === worst ? k.gold : "#F3C56A"} />
          </g>
        );
      })}
    </svg>
  );
}

export function PeopleDots({ t, tracked, total }) {
  const k = inks(t);
  const n = Math.max(0, Math.min(48, total || 0));
  const on = Math.max(0, Math.min(n, tracked || 0));
  return (
    <div className="tgw-dots" style={{ display: "flex", flexWrap: "wrap", gap: 7, padding: "10px 0 4px" }}>
      {Array.from({ length: n }, (_, i) => (
        <i
          key={i}
          className="tgw-pop"
          title={i < on ? "Tracked" : "Silent"}
          style={{
            width: 15, height: 15, borderRadius: 99,
            background: i < on ? k.blue : t.c.border,
            boxShadow: i < on ? `0 0 0 4px ${k.blue}28` : "none",
            animationDelay: `${0.1 + i * 0.028}s`,
          }}
        />
      ))}
    </div>
  );
}

export function ProjectArcs({ t, projects }) {
  const colors = toolColors(t);
  const items = (projects || []).slice(0, 4);
  const total = items.reduce((a, p) => a + (p.minutes || 0), 0) || 1;
  return (
    <svg viewBox="0 0 230 122" width="100%" height="122">
      {items.map((p, i) => {
        const r = 50 - i * 9;
        const circ = 2 * Math.PI * r;
        const pct = (p.minutes || 0) / total;
        return (
          <circle
            key={p.name}
            cx="72" cy="60" r={r} fill="none" stroke={colors[i]} strokeWidth="7" strokeLinecap="round"
            strokeDasharray={`${circ * pct} ${circ}`} transform="rotate(-90 72 60)"
            opacity={1 - i * 0.06}
          >
            <title>{`${p.name} · ${fmtH(p.minutes)}`}</title>
          </circle>
        );
      })}
    </svg>
  );
}

export function PolarClock({ t, hourPeople }) {
  const k = inks(t);
  const HOURS = hourPeople || [];
  if (HOURS.length !== 24) return null;
  const W = 360, H = 340, cx = 180, cy = 168, r0 = 46, rMax = 132;
  const mx = Math.max(...HOURS, 0.01);
  const peak = HOURS.indexOf(Math.max(...HOURS));
  const wedge = (r1, a0, a1) => {
    const p = (r, a) => [cx + r * Math.cos(a), cy + r * Math.sin(a)];
    const [x0, y0] = p(r0, a0);
    const [x1, y1] = p(r1, a0);
    const [x2, y2] = p(r1, a1);
    const [x3, y3] = p(r0, a1);
    return `M${x0.toFixed(1)} ${y0.toFixed(1)} L${x1.toFixed(1)} ${y1.toFixed(1)} A${r1} ${r1} 0 0 1 ${x2.toFixed(1)} ${y2.toFixed(1)} L${x3.toFixed(1)} ${y3.toFixed(1)} A${r0} ${r0} 0 0 0 ${x0.toFixed(1)} ${y0.toFixed(1)} Z`;
  };
  const mix = (tt) => {
    const a = t.isDark ? [40, 46, 58] : [232, 236, 244];
    const b = t.isDark ? [120, 159, 232] : [51, 95, 181];
    const c = a.map((v, i) => Math.round(v + (b[i] - v) * tt));
    return `rgb(${c[0]},${c[1]},${c[2]})`;
  };
  return (
    <svg className="tgw-clock" viewBox={`0 0 ${W} ${H}`} width="100%" height={H}>
      <circle cx={cx} cy={cy} r={rMax + 2} fill="none" stroke={t.c.border} strokeWidth="1" />
      {HOURS.map((v, h) => {
        const tt = v / mx;
        const r1 = r0 + 18 + tt * (rMax - r0 - 18);
        const a0 = (h / 24) * Math.PI * 2 - Math.PI / 2;
        const a1 = ((h + 1) / 24) * Math.PI * 2 - Math.PI / 2 - 0.012;
        return (
          <path
            key={h}
            className={`tgw-petal${h === peak ? " is-peak" : ""}`}
            d={wedge(r1, a0, a1)}
            fill={h === peak ? k.blueD : mix(0.25 + tt * 0.75)}
            style={{ animationDelay: h === peak ? `${0.08 + h * 0.018}s, 1s` : `${0.08 + h * 0.018}s` }}
          >
            <title>{`${hhLabel(h)} · ${v.toFixed(1)} people`}</title>
          </path>
        );
      })}
      <line className="tgw-hand" x1={cx} y1={cy} x2={cx} y2={cy - rMax} stroke={k.blue} strokeWidth="2" strokeLinecap="round" />
      <circle cx={cx} cy={cy} r={r0 - 2} fill={paper(t).card} />
      <text x={cx} y={cy - 4} textAnchor="middle" fontSize="20" fontWeight="720" fill={k.ink}>{hhLabel(peak).replace("a", " AM").replace("p", " PM")}</text>
      <text x={cx} y={cy + 14} textAnchor="middle" fontSize="10" fontWeight="700" fill={k.dim}>PEAK · {HOURS[peak].toFixed(1)}</text>
      {[[0, "12a"], [6, "6a"], [12, "12p"], [18, "6p"]].map(([h, lab]) => {
        const a = (h / 24) * Math.PI * 2 - Math.PI / 2;
        const r = rMax + 14;
        return (
          <text key={h} x={(cx + r * Math.cos(a)).toFixed(1)} y={(cy + r * Math.sin(a) + 4).toFixed(1)}
            textAnchor="middle" fontSize="11" fontWeight="650" fill={k.dim}>{lab}</text>
        );
      })}
    </svg>
  );
}

export function DayCurve({ t, hourPeople }) {
  const k = inks(t);
  const HOURS = hourPeople || [];
  if (HOURS.length !== 24) return null;
  const W = 520, H = 188, pL = 10, pR = 10, pT = 28, pB = 20;
  const mx = Math.max(...HOURS, 0.01);
  const pts = HOURS.map((v, h) => [pL + (h / 23) * (W - pL - pR), pT + (1 - v / mx) * (H - pT - pB)]);
  const peak = HOURS.indexOf(Math.max(...HOURS));
  const pk = pts[peak];
  return (
    <>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H}>
        <defs>
          <linearGradient id="tgwCf" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={k.blue} stopOpacity=".26" /><stop offset="100%" stopColor={k.blue} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={smooth(pts, H - pB)} fill="url(#tgwCf)" />
        <path d={smooth(pts)} fill="none" stroke={k.blue} strokeWidth="2.6" />
        {pts.filter((_, i) => i % 2 === 0).map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r="2.8" fill={t.c.surface} stroke={k.blue} strokeWidth="1.6">
            <title>{`${hhLabel(i * 2)} · ${HOURS[i * 2].toFixed(1)} people`}</title>
          </circle>
        ))}
        <circle cx={pk[0]} cy={pk[1]} r="5.5" fill={k.blue} />
        <rect x={pk[0] - 36} y={pk[1] - 30} rx="10" width="72" height="18" fill={k.blue} />
        <text x={pk[0]} y={pk[1] - 17} textAnchor="middle" fontSize="10" fontWeight="750" fill="#fff">
          {hhLabel(peak)} · {HOURS[peak].toFixed(1)}
        </text>
      </svg>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10.5, color: k.dim, padding: "0 8px 4px" }}>
        <span>12a</span><span>6a</span><span>12p</span><span>6p</span><span>11p</span>
      </div>
    </>
  );
}

export function toolMinutes(x) {
  if (!x) return 0;
  if (Number(x.minutes) > 0) return Number(x.minutes);
  if (Array.isArray(x.sparkMinutes)) return x.sparkMinutes.reduce((a, n) => a + (Number(n) || 0), 0);
  return 0;
}

const TOOL_FOLD = [
  { key: "chrome", rx: /^(google chrome|chrome|chromium|chrome\.exe|google-chrome)$/i, label: "Chrome" },
  { key: "edge", rx: /^(microsoft edge|edge|msedge|msedge\.exe|microsoft-edge)$/i, label: "Edge" },
  { key: "firefox", rx: /^(mozilla firefox|firefox|firefox\.exe)$/i, label: "Firefox" },
  { key: "safari", rx: /^(safari|safari\.exe)$/i, label: "Safari" },
  { key: "brave", rx: /^(brave browser|brave|brave\.exe)$/i, label: "Brave" },
  { key: "claude", rx: /^claude(\.exe|\s*ai)?$/i, label: "Claude" },
  { key: "teams", rx: /^(microsoft teams|teams|ms-teams|teams\.exe)$/i, label: "Teams" },
  { key: "explorer", rx: /^(file explorer|explorer(\.exe)?|windows explorer)$/i, label: "File Explorer" },
];

function prettyToolName(raw) {
  const s = String(raw || "").trim();
  if (!s) return s;
  if (s === s.toLowerCase()) return s.replace(/\b[a-z]/g, (c) => c.toUpperCase());
  return s;
}

/** Claude + claude, Chrome + Google Chrome — one row, minutes summed. */
export function foldTools(tools) {
  const map = new Map();
  for (const x of tools || []) {
    const mins = toolMinutes(x);
    if (!x?.name || mins <= 0) continue;
    const raw = String(x.name).trim();
    const hit = TOOL_FOLD.find((f) => f.rx.test(raw));
    const key = hit ? hit.key : raw.toLowerCase();
    const name = hit ? hit.label : prettyToolName(raw);
    const cur = map.get(key);
    if (!cur) {
      map.set(key, { ...x, name, minutes: mins, sparkMinutes: [...(x.sparkMinutes || [])] });
      continue;
    }
    cur.minutes += mins;
    const a = cur.sparkMinutes || [];
    const b = x.sparkMinutes || [];
    const n = Math.max(a.length, b.length);
    if (n) cur.sparkMinutes = Array.from({ length: n }, (_, i) => (a[i] || 0) + (b[i] || 0));
  }
  return [...map.values()].sort((a, b) => b.minutes - a.minutes);
}

/** C stack: rings left, chip rows right. >6 tools → top 5 + Other. */
export function ToolRings({ t, tools, onTool, fill }) {
  const colors = toolColors(t);
  const k = inks(t);
  const ppr = paper(t);
  const folded = foldTools(tools);
  const extras = folded.length > 6 ? folded.slice(5) : [];
  const items = extras.length
    ? [...folded.slice(0, 5), {
      name: `+${extras.length} more`,
      minutes: extras.reduce((a, x) => a + (x.minutes || 0), 0),
      type: "other",
      otherNames: extras.map((x) => x.name),
    }]
    : folded;
  const total = folded.reduce((a, x) => a + x.minutes, 0) || 1;
  const top = items[0];
  const topPct = top ? Math.round((top.minutes / total) * 100) : 0;
  const S = 280;
  const cx = 140;
  const cy = 140;
  const stroke = 12;
  const step = 17;
  const outer = 112;
  return (
    <div className={`tgw-tools-c${fill ? " is-fill" : ""}`} style={{ "--n": items.length }}>
      <div className="tgw-tools-rings">
        <svg className="tgw-rings" viewBox={`0 0 ${S} ${S}`} width="100%" height="100%">
          <g className="tgw-rings-spin" style={{ transformOrigin: `${cx}px ${cy}px` }}>
            {items.map((x, i) => {
              const r = outer - i * step;
              const circ = 2 * Math.PI * r;
              const pct = (x.minutes || 0) / total;
              const shown = Math.max(circ * pct, 0.01);
              const hue = x.type === "other" ? k.dim : colors[i];
              const click = x.type !== "other" && onTool ? () => onTool(x) : undefined;
              return (
                <g key={x.name} onClick={click} style={{ cursor: click ? "pointer" : "default" }}>
                  <circle cx={cx} cy={cy} r={r} fill="none" stroke={t.c.border} strokeWidth={stroke} />
                  <circle
                    className="tgw-ring"
                    cx={cx} cy={cy} r={r} fill="none" stroke={hue} strokeWidth={stroke} strokeLinecap="round"
                    strokeDasharray={`${shown} ${circ}`} strokeDashoffset={shown}
                    transform={`rotate(-90 ${cx} ${cy})`}
                    style={{ animationDelay: `${0.12 + i * 0.12}s` }}
                  />
                  <title>{`${x.name} · ${fmtH(x.minutes)} · ${Math.round(pct * 100)}%`}</title>
                </g>
              );
            })}
          </g>
          {top && (
            <>
              <text x={cx} y={cy - 4} textAnchor="middle" fontSize="22" fontWeight="750" fill={k.ink} fontFamily={t.font.family}>
                {topPct}%
              </text>
              <text x={cx} y={cy + 14} textAnchor="middle" fontSize="10" fontWeight="700" fill={k.dim} letterSpacing="0.6">
                {String(top.name).slice(0, 14)}
              </text>
            </>
          )}
        </svg>
      </div>
      <div className="tgw-tools-stack">
        {items.map((x, i) => {
          const pct = Math.round(((x.minutes || 0) / total) * 100);
          const hue = x.type === "other" ? k.dim : colors[i];
          const wash = t.isDark
            ? `color-mix(in srgb, ${hue} 20%, ${ppr.well})`
            : `color-mix(in srgb, ${hue} 12%, ${ppr.well})`;
          const click = x.type !== "other" && onTool ? () => onTool(x) : undefined;
          const chip = (
            <button
              key={x.name}
              type="button"
              className="tgw-tl-card tgw-tool-chip"
              onClick={click}
              style={{
                border: `1px solid color-mix(in srgb, ${hue} 32%, ${t.c.border})`,
                background: wash, cursor: click ? "pointer" : "default",
                fontFamily: "inherit", color: "inherit",
              }}
            >
              {x.type === "other" ? (
                <span style={{
                  width: 32, height: 32, borderRadius: 9, flexShrink: 0, display: "grid", placeItems: "center",
                  fontSize: 12, fontWeight: 800, color: hue, background: t.isDark ? ppr.well : "#fff",
                }}>+{extras.length}</span>
              ) : (
                <ToolIcon name={x.name} type={x.type} size={32} />
              )}
              <span style={{ minWidth: 0, flex: "0 1 108px", textAlign: "left" }}>
                <span style={{
                  display: "block", fontSize: 13, fontWeight: 700, letterSpacing: "-0.15px",
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>{x.name}</span>
                <span style={{ display: "block", marginTop: 2, fontSize: 11.5, color: t.c.textSecondary, fontVariantNumeric: "tabular-nums" }}>
                  {fmtH(x.minutes)}
                </span>
              </span>
              <span style={{ flex: 1, minWidth: 28, height: 6, borderRadius: 99, background: t.c.border, overflow: "hidden" }}>
                <i style={{ display: "block", height: "100%", width: `${Math.max(4, pct)}%`, background: hue, borderRadius: 99 }} />
              </span>
              <span style={{
                flexShrink: 0, fontSize: 15, fontWeight: 750, letterSpacing: "-0.3px",
                fontVariantNumeric: "tabular-nums", color: hue,
              }}>{pct}%</span>
            </button>
          );
          if (x.otherNames?.length) {
            return (
              <Tooltip key={x.name} title={x.otherNames.join(" · ")}>
                {chip}
              </Tooltip>
            );
          }
          return chip;
        })}
      </div>
    </div>
  );
}

export function ProjectBubbles({ t, projects, onProject }) {
  const colors = toolColors(t);
  const items = [...(projects || [])].sort((a, b) => (b.minutes || 0) - (a.minutes || 0)).slice(0, 4);
  const total = items.reduce((a, p) => a + (p.minutes || 0), 0) || 1;
  const pack = [
    [118, 114, 62],
    [208, 80, 48],
    [198, 164, 38],
    [80, 186, 32],
  ];
  return (
    <svg className="tgw-bub" viewBox="0 0 300 236" width="100%" height="236">
      {items.map((p, i) => {
        const [x, y, r] = pack[i] || pack[3];
        const pct = Math.round(((p.minutes || 0) / total) * 100);
        return (
          <g key={p.projectId || p.name} onClick={() => onProject?.(p)} className="tgw-pop"
            style={{ animationDelay: `${0.16 + i * 0.08}s`, cursor: onProject ? "pointer" : "default" }}>
            <title>{`${p.name} · ${fmtH(p.minutes)} · ${pct}%`}</title>
            <circle cx={x + 3} cy={y + 5} r={r} fill={inks(t).ink} opacity=".07" />
            <circle cx={x} cy={y} r={r} fill={colors[i]} />
            <circle cx={x - r * 0.3} cy={y - r * 0.34} r={r * 0.5} fill="#fff" opacity=".18" />
            {r > 40 ? (
              <>
                <circle cx={x} cy={y - r * 0.42} r={r * 0.19} fill="#fff" opacity=".92" />
                <text x={x} y={y - r * 0.42 + r * 0.065} textAnchor="middle"
                  fontSize={Math.max(8, r * 0.15)} fontWeight="800" fill={colors[i]}>
                  {(p.name || "?").trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase()}
                </text>
              </>
            ) : null}
            <text x={x} y={y + (r > 40 ? 6 : -1)} textAnchor="middle" fontSize={r > 45 ? 18 : 13} fontWeight="750" fill="#fff">{pct}%</text>
            <text x={x} y={y + (r > 40 ? 22 : 16)} textAnchor="middle" fontSize="11" fontWeight="600" fill="#fff">
              {(p.name || "").split(" ")[0]}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

/**
 * Project share as a composition ramp + ranked rows. Replaces the bubble
 * pack on the panels: circle areas are hard to compare by eye, names
 * truncated to one word, and anything past four projects vanished. The
 * ramp shows the split at a glance; the rows carry full names, real
 * hours, share, and the click-through. Row ↔ segment cross-light.
 */
export function ProjectRamp({ t, projects, onProject }) {
  const colors = toolColors(t);
  const [hot, setHot] = React.useState(null);
  const items = [...(projects || [])]
    .filter((p) => p && p.name && (p.minutes || 0) > 0)
    .sort((a, b) => (b.minutes || 0) - (a.minutes || 0));
  const total = items.reduce((a, p) => a + (p.minutes || 0), 0);
  if (!items.length || total <= 0) return null;
  const shown = items.slice(0, 5);
  const rest = items.length - shown.length;
  const restMin = items.slice(5).reduce((a, p) => a + (p.minutes || 0), 0);
  return (
    <div onMouseLeave={() => setHot(null)}>
      <div style={{ display: "flex", height: 14, borderRadius: 99, overflow: "hidden", gap: 2, margin: "4px 0 14px" }}>
        {shown.map((p, i) => (
          <i
            key={p.projectId || p.name}
            className="tgw-growX"
            onMouseEnter={() => setHot(i)}
            style={{
              display: "block", width: `${((p.minutes || 0) / total) * 100}%`,
              background: colors[i % colors.length],
              opacity: hot == null || hot === i ? 1 : 0.25,
              transition: "opacity .15s ease",
              animationDelay: `${0.1 + i * 0.08}s`,
            }}
          />
        ))}
        {rest > 0 ? (
          <i aria-hidden="true" style={{ display: "block", width: `${(restMin / total) * 100}%`, background: t.c.border }} />
        ) : null}
      </div>
      <div className="tgw-race">
        {shown.map((p, i) => {
          const pct = Math.round(((p.minutes || 0) / total) * 100);
          return (
            <button
              key={p.projectId || p.name}
              type="button"
              className="tgw-row"
              onClick={() => onProject?.(p)}
              onMouseEnter={() => setHot(i)}
              style={{
                display: "flex", alignItems: "center", gap: 10, width: "100%",
                padding: "8px 6px", textAlign: "left", border: 0, borderRadius: 10,
                background: hot === i ? paper(t).well : "none",
                transition: "background .15s ease",
                cursor: onProject ? "pointer" : "default", fontFamily: "inherit", color: "inherit",
              }}
            >
              <i aria-hidden="true" style={{ width: 9, height: 9, borderRadius: 99, background: colors[i % colors.length], flexShrink: 0 }} />
              <MonoTile t={t} name={p.name} size={22} />
              <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</span>
              <b style={{ fontSize: 12.5, fontVariantNumeric: "tabular-nums" }}>{fmtMins(p.minutes)}</b>
              <span style={{ minWidth: 36, textAlign: "right", fontSize: 12, fontWeight: 650, color: t.c.textSecondary, fontVariantNumeric: "tabular-nums" }}>{pct}%</span>
              {onProject ? <span style={{ color: t.c.textTertiary, fontSize: 15 }}>›</span> : null}
            </button>
          );
        })}
        {rest > 0 ? (
          <div style={{ fontSize: 11.5, color: t.c.textTertiary, padding: "6px 6px 0" }}>
            + {rest} more · {fmtMins(restMin)}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function pageWindow(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i);
  const keep = new Set([0, total - 1, current, current - 1, current + 1]);
  if (current <= 2) [0, 1, 2, 3, 4].forEach((i) => keep.add(i));
  if (current >= total - 3) [total - 5, total - 4, total - 3, total - 2, total - 1].forEach((i) => keep.add(i));
  const sorted = [...keep].filter((i) => i >= 0 && i < total).sort((a, b) => a - b);
  const out = [];
  sorted.forEach((n, i) => {
    if (i && n - sorted[i - 1] > 1) out.push("gap");
    out.push(n);
  });
  return out;
}

function PagerCell({ t, disabled, active, onClick, label, children }) {
  const [hot, setHot] = React.useState(false);
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      aria-label={label}
      aria-current={active ? "page" : undefined}
      onMouseEnter={() => !disabled && setHot(true)}
      onMouseLeave={() => setHot(false)}
      style={{
        width: 36, height: 36, padding: 0, border: "none", borderRadius: 0,
        background: active ? t.c.primary : hot && !disabled ? (t.isDark ? "rgba(255,255,255,.08)" : "rgba(24,32,46,.06)") : "transparent",
        color: active ? "#fff" : disabled ? t.c.textTertiary : t.c.text,
        cursor: disabled ? "default" : "pointer",
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        fontSize: 13, fontWeight: active ? 750 : 650, fontFamily: "inherit",
        fontVariantNumeric: "tabular-nums", lineHeight: 1,
        transition: "background .15s ease",
      }}
    >
      {children}
    </button>
  );
}

function PagerRule({ t }) {
  return <i aria-hidden="true" style={{ width: 1, height: 18, background: t.c.border, flexShrink: 0 }} />;
}

/** One segmented control: ‹ · 1 · 2 · › — not a stretched bar. */
export function Pager({ t, page, pages, onPage, flush }) {
  if (!pages || pages <= 1) return null;
  const ppr = paper(t);
  const cluster = (
    <div style={{
      display: "inline-flex", alignItems: "center",
      height: 36, borderRadius: 10, overflow: "hidden",
      background: ppr.card, border: `1px solid ${t.c.border}`,
      boxShadow: `0 1px 2px ${t.c.shadowColor || "rgba(24,32,46,.06)"}`,
    }}>
      {pages > 3 && (
        <>
          <PagerCell t={t} disabled={page <= 0} onClick={() => onPage(0)} label="First page">
            <ChevronsLeft size={15} strokeWidth={2.2} />
          </PagerCell>
          <PagerRule t={t} />
        </>
      )}
      <PagerCell t={t} disabled={page <= 0} onClick={() => onPage(page - 1)} label="Previous page">
        <ChevronLeft size={16} strokeWidth={2.2} />
      </PagerCell>
      <PagerRule t={t} />
      {pageWindow(page, pages).map((n, i) => (
        n === "gap" ? (
          <React.Fragment key={`g${i}`}>
            <span style={{ width: 28, textAlign: "center", color: t.c.textTertiary, fontSize: 13, fontWeight: 700 }}>…</span>
            <PagerRule t={t} />
          </React.Fragment>
        ) : (
          <React.Fragment key={n}>
            <PagerCell t={t} active={n === page} onClick={() => onPage(n)} label={`Page ${n + 1}`}>
              {n + 1}
            </PagerCell>
            <PagerRule t={t} />
          </React.Fragment>
        )
      ))}
      <PagerCell t={t} disabled={page >= pages - 1} onClick={() => onPage(page + 1)} label="Next page">
        <ChevronRight size={16} strokeWidth={2.2} />
      </PagerCell>
      {pages > 3 && (
        <>
          <PagerRule t={t} />
          <PagerCell t={t} disabled={page >= pages - 1} onClick={() => onPage(pages - 1)} label="Last page">
            <ChevronsRight size={15} strokeWidth={2.2} />
          </PagerCell>
        </>
      )}
    </div>
  );
  return (
    <nav
      aria-label="Pagination"
      style={flush ? {
        display: "flex", justifyContent: "flex-end",
        margin: "auto -18px 0",
        padding: "10px 14px 12px",
        borderTop: `1px solid ${t.c.border}`,
        borderBottomLeftRadius: t.r.card,
        borderBottomRightRadius: t.r.card,
      } : {
        display: "flex", justifyContent: "flex-end", marginTop: 10,
      }}
    >
      {cluster}
    </nav>
  );
}

/* ── shaped loading skeletons ────────────────────────────────────────────
   One shimmer system (.tgw-shimmer), shaped like what actually arrives —
   a donut loads as a ring, hours load as bars, people load as rows. A
   featureless grey slab tells the reader nothing about what to expect. */

function shimVars(t) {
  const ppr = paper(t);
  return { "--tgw-sh0": ppr.well, "--tgw-sh1": t.c.primarySoft || t.c.border };
}

export function SkelBox({ t, w = "100%", h = 12, r = 8, delay = 0, style }) {
  return (
    <span
      aria-hidden="true"
      className="tgw-shimmer"
      style={{ ...shimVars(t), display: "block", width: w, height: h, borderRadius: r, animationDelay: `${delay}s`, ...style }}
    />
  );
}

/** RaceRows-shaped: avatar circle + name line + value bar, per row. */
export function SkelPeople({ t, count = 6 }) {
  return (
    <div aria-hidden="true" style={{ padding: "4px 0" }}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} style={{ display: "grid", gridTemplateColumns: "26px 1fr", gap: 10, alignItems: "center", padding: "7px 6px" }}>
          <SkelBox t={t} w={26} h={26} r={99} delay={i * 0.07} />
          <span>
            <SkelBox t={t} w={`${34 + ((i * 13) % 30)}%`} h={10} r={5} delay={i * 0.07} />
            <SkelBox t={t} h={11} r={99} delay={i * 0.07} style={{ marginTop: 6 }} />
          </span>
        </div>
      ))}
    </div>
  );
}

/** Donut/rings-shaped: shimmering ring with a true hole + legend rows. */
export function SkelDonut({ t, legend = 5, size = 188 }) {
  const hole = Math.round(size * 0.52);
  return (
    <div aria-hidden="true" style={{ display: "flex", alignItems: "center", gap: 22, flexWrap: "wrap" }}>
      <span style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
        <SkelBox t={t} w={size} h={size} r="50%" />
        <span style={{
          position: "absolute", left: "50%", top: "50%", transform: "translate(-50%,-50%)",
          width: hole, height: hole, borderRadius: "50%", background: paper(t).card,
        }} />
      </span>
      {legend > 0 ? (
        <span style={{ flex: 1, minWidth: 200 }}>
          {Array.from({ length: legend }).map((_, i) => (
            <SkelBox key={i} t={t} h={30} r={10} delay={i * 0.08} style={{ marginBottom: 8 }} />
          ))}
        </span>
      ) : null}
    </div>
  );
}

/** Hour/day-columns-shaped: a skyline of bars, tallest mid-day. */
export function SkelBars({ t, bars = 24, height = 190 }) {
  return (
    <div aria-hidden="true" style={{ display: "flex", alignItems: "flex-end", gap: "0.55%", height, padding: "10px 0 4px" }}>
      {Array.from({ length: bars }).map((_, i) => {
        const hPct = 18 + Math.round(62 * Math.abs(Math.sin((i / bars) * Math.PI)));
        return (
          <span key={i} style={{ flex: 1, minWidth: 0, display: "flex", justifyContent: "center", alignItems: "flex-end", height: "100%" }}>
            <SkelBox t={t} w="min(58%, 22px)" h={`${hPct}%`} r={99} delay={i * 0.03} />
          </span>
        );
      })}
    </div>
  );
}

/** Focus-flow-shaped: chips on both banks, blank rails between. */
export function SkelFlow({ t, rows = 4 }) {
  const col = (side) => (
    <span style={{ display: "flex", flexDirection: "column", gap: 16, padding: "8px 0", flexShrink: 0 }}>
      {Array.from({ length: rows }).map((_, i) => (
        <SkelBox key={`${side}${i}`} t={t} w={120} h={30} r={99} delay={i * 0.08} />
      ))}
    </span>
  );
  return (
    <div aria-hidden="true" style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
      {col("l")}
      {col("r")}
    </div>
  );
}

/**
 * Project share as a donut with a live centre. The centre resting state is
 * the honest total; hovering an arc or a legend row swaps it for that
 * project's share. Legend rows carry full names, real hours and the
 * click-through — the donut alone never has to carry the labels.
 */
export function ProjectDonut({ t, projects, onProject }) {
  const colors = toolColors(t);
  const k = inks(t);
  /* hot: null = resting · -1 = the "rest" arc · n = items[n] */
  const [hot, setHot] = React.useState(null);
  const [page, setPage] = React.useState(0);
  const items = [...(projects || [])]
    .filter((p) => p && p.name && (p.minutes || 0) > 0)
    .sort((a, b) => (b.minutes || 0) - (a.minutes || 0));
  const total = items.reduce((a, p) => a + (p.minutes || 0), 0);
  const count = items.length;
  React.useEffect(() => { setPage(0); }, [count]);
  if (!count || total <= 0) return null;

  const shown = items.slice(0, 6);
  const restMin = items.slice(6).reduce((a, p) => a + (p.minutes || 0), 0);
  const slices = restMin > 0
    ? [...shown, { name: `${count - shown.length} more`, minutes: restMin, _rest: true }]
    : shown;

  const R = 74;
  const C = 2 * Math.PI * R;
  const GAP = slices.length > 1 ? 3 : 0;
  let acc = 0;
  const arcs = slices.map((p, i) => {
    const frac = (p.minutes || 0) / total;
    const start = acc;
    acc += frac;
    return { p, i, frac, start };
  });

  /* Which arc lights up for the current hot row. Rows folded into the
     "rest" slice light that slice. */
  const restArcIdx = restMin > 0 ? slices.length - 1 : null;
  const hotArcIdx = hot == null ? null : hot === -1 ? restArcIdx : hot < shown.length ? hot : restArcIdx;

  const hotItem = hot != null && hot >= 0 ? items[hot] : null;
  const centerTop = hotItem
    ? `${Math.round(((hotItem.minutes || 0) / total) * 100)}%`
    : hot === -1
      ? `${Math.round((restMin / total) * 100)}%`
      : fmtH(total);
  const centerSub = hotItem
    ? String(hotItem.name).split(" ").slice(0, 2).join(" ").slice(0, 16)
    : hot === -1
      ? `${count - shown.length} more projects`
      : "total tracked";

  const PAGE = 5;
  const pages = Math.max(1, Math.ceil(count / PAGE));
  const safePage = Math.min(page, pages - 1);
  const rows = items.slice(safePage * PAGE, (safePage + 1) * PAGE);

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, width: "100%", paddingBottom: pages > 1 ? 0 : 17 }}>
    <div
      style={{ display: "flex", alignItems: "center", gap: 22, flexWrap: "wrap", flex: 1, minHeight: 0 }}
      onMouseLeave={() => setHot(null)}
    >
      {/* No .tgw-ring here: its draw-in keyframe forces stroke-dashoffset to 0,
          which stacks every slice at 12 o'clock. The svg pops in as one. */}
      <svg viewBox="0 0 200 200" width="188" height="188" className="tgw-rings-spin" style={{ flexShrink: 0 }}>
        <g transform="rotate(-90 100 100)">
          {arcs.map(({ p, i, frac, start }) => (
            <circle
              key={p.projectId || p.name}
              cx="100" cy="100" r={R}
              fill="none"
              stroke={p._rest ? t.c.border : colors[i % colors.length]}
              strokeWidth={hotArcIdx === i ? 30 : 25}
              strokeDasharray={`${Math.max(1, frac * C - GAP)} ${C}`}
              strokeDashoffset={-start * C}
              strokeLinecap="butt"
              opacity={hotArcIdx == null || hotArcIdx === i ? 1 : 0.22}
              onMouseEnter={() => setHot(p._rest ? -1 : i)}
              onClick={!p._rest && onProject ? () => onProject(p) : undefined}
              style={{
                cursor: !p._rest && onProject ? "pointer" : "default",
                transition: "opacity .15s ease, stroke-width .15s ease",
              }}
            >
              <title>{`${p.name} · ${fmtMins(p.minutes)} · ${Math.round(frac * 100)}%`}</title>
            </circle>
          ))}
        </g>
        <text x="100" y="97" textAnchor="middle" fontSize="24" fontWeight="750" fill={k.ink} style={{ fontVariantNumeric: "tabular-nums" }}>
          {centerTop}
        </text>
        <text x="100" y="116" textAnchor="middle" fontSize="10.5" fontWeight="700" letterSpacing="0.6" fill={k.dim}>
          {centerSub}
        </text>
      </svg>

      <div style={{ flex: 1, minWidth: 200 }}>
        <div className="tgw-race">
          {rows.map((p, j) => {
            const g = safePage * PAGE + j; // global rank
            const inTop = g < shown.length;
            const pct = Math.round(((p.minutes || 0) / total) * 100);
            return (
              <button
                key={p.projectId || p.name}
                type="button"
                className="tgw-row"
                onClick={onProject ? () => onProject(p) : undefined}
                onMouseEnter={() => setHot(g)}
                style={{
                  display: "flex", alignItems: "center", gap: 10, width: "100%",
                  padding: "7px 8px", textAlign: "left", border: 0, borderRadius: 10,
                  background: hot === g ? paper(t).well : "none",
                  transition: "background .15s ease",
                  cursor: onProject ? "pointer" : "default",
                  fontFamily: "inherit", color: "inherit",
                }}
              >
                <span style={{ width: 18, fontSize: 10.5, fontWeight: 800, color: t.c.textTertiary, fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>
                  {String(g + 1).padStart(2, "0")}
                </span>
                <i aria-hidden="true" style={{ width: 9, height: 9, borderRadius: 99, background: inTop ? colors[g % colors.length] : t.c.border, flexShrink: 0 }} />
                <MonoTile t={t} name={p.name} size={22} />
                <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</span>
                <b style={{ fontSize: 12.5, fontVariantNumeric: "tabular-nums" }}>{fmtMins(p.minutes)}</b>
                <span style={{ minWidth: 36, textAlign: "right", fontSize: 12, fontWeight: 650, color: t.c.textSecondary, fontVariantNumeric: "tabular-nums" }}>{pct}%</span>
                {onProject ? <span style={{ color: t.c.textTertiary, fontSize: 15 }}>›</span> : null}
              </button>
            );
          })}
        </div>
      </div>
    </div>
      <Pager t={t} page={safePage} pages={pages} onPage={setPage} flush />
    </div>
  );
}

/**
 * Generic identity tile: brand colour when the name is a known app, the
 * deterministic accent pair otherwise. shape "circle" for people, rounded
 * rect for tools/projects — one identity system, everywhere.
 */
export function MonoTile({ t, name, size = 22, shape = "rect" }) {
  const brand = brandColorFor(name, t);
  const pair = !brand && t.accentFor ? t.accentFor(String(name || "")) : null;
  const bg = brand || (pair ? pair.bg : t.c.primarySoft);
  const fg = brand ? glyphColor(brand) : (pair ? pair.fg : t.c.primary);
  const mono = String(name || "?").trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
  return (
    <span aria-hidden="true" style={{
      width: size, height: size, borderRadius: shape === "circle" ? 999 : Math.max(5, size * 0.28),
      background: bg, color: fg, display: "inline-flex", alignItems: "center",
      justifyContent: "center", flexShrink: 0, fontSize: size * 0.38, fontWeight: 800,
      letterSpacing: 0.2, boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.06)",
    }}>{mono}</span>
  );
}

export function RaceRows({ t, people, onPerson, onIdle, columns = 1 }) {
  const k = inks(t);
  const max = Math.max(...(people || []).map((p) => p.totalMinutes || 0), 1);
  const twin = columns > 1;
  return (
    <div
      className={twin ? "tgw-tl tgw-people-2" : "tgw-race"}
      style={twin
        ? { display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: "2px 14px", padding: "2px 0 0" }
        : { padding: "2px 0 0" }}
    >
      {(people || []).map((p, i) => {
        const tot = p.totalMinutes || 0;
        const w = tot ? Math.max(12, (tot / max) * 100) : 8;
        const ini = p.name.split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();
        const av = t.accentFor ? t.accentFor(String(p.name || "")) : null;
        return (
          <button
            key={p.userId}
            type="button"
            className="tgw-row"
            onClick={() => onPerson?.(p)}
            style={{
              display: "grid", gridTemplateColumns: "22px 1fr", gap: 8, alignItems: "center",
              width: "100%", padding: "5px 4px", textAlign: "left", border: 0, background: "none",
              cursor: onPerson ? "pointer" : "default", fontFamily: "inherit", color: "inherit",
            }}
          >
            <span style={{
              width: 22, height: 22, borderRadius: 99, display: "grid", placeItems: "center",
              fontSize: 8, fontWeight: 800, flexShrink: 0,
              background: av ? av.bg : t.c.primarySoft, color: av ? av.fg : k.blue,
            }}>{ini}</span>
            <span style={{ minWidth: 0 }}>
              <span style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                gap: 8, fontSize: 12, marginBottom: 3,
              }}>
                <b style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {p.name}
                </b>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                  {/* Idle is the number people act on, so it gets its own target.
                      A <span role="button"> and not a <button>: the whole row is
                      already a button and nesting one inside is invalid HTML. */}
                  {/* Resting affordance, not hover-only: a transparent chip in a
                      column of eight rows reads as a label, and nobody clicks a
                      label. The wash is the SAME gold as the idle segment in the
                      bar right beneath it, so the tint doubles as the legend. */}
                  {onIdle && p.idleMinutes > 0 ? (
                    <span
                      className="tgw-idle-chip"
                      role="button"
                      tabIndex={0}
                      title={`${fmtMins(p.idleMinutes)} idle — open idle reasons`}
                      style={{
                        background: `${k.gold}1F`,
                        borderColor: `${k.gold}59`,
                        color: k.gold,
                      }}
                      onClick={(e) => { e.stopPropagation(); onIdle(p); }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault(); e.stopPropagation(); onIdle(p);
                        }
                      }}
                    >
                      <i style={{ background: k.gold }} />
                      {fmtMins(p.idleMinutes)} idle
                      {/* The chevron is what turns a badge into a control. Same
                          cue the toolbar chips use for "this opens a panel". */}
                      <svg
                        width="9" height="9" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="3.2"
                        strokeLinecap="round" strokeLinejoin="round"
                        style={{ opacity: .75, marginLeft: 1 }}
                        aria-hidden="true"
                      >
                        <path d="m9 18 6-6-6-6" />
                      </svg>
                    </span>
                  ) : null}
                  <span style={{ color: k.dim, fontVariantNumeric: "tabular-nums" }}>{tot ? fmtMins(tot) : "silent"}</span>
                </span>
              </span>
              <span style={{ display: "block", height: 8, borderRadius: 99, background: t.c.surfaceMuted, overflow: "hidden" }}>
                <span className="tgw-growX" style={{ display: "flex", width: `${w}%`, height: "100%", animationDelay: `${0.22 + i * 0.07}s` }}>
                  {tot ? (
                    <>
                      <i style={{ width: `${(p.activeMinutes / tot) * 100}%`, background: k.blue }} />
                      <i style={{ width: `${(p.idleMinutes / tot) * 100}%`, background: k.gold }} />
                    </>
                  ) : <i style={{ width: "100%", background: t.c.border }} />}
                </span>
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * Focus flow — HTML chips on both banks, stretchy SVG rails between them.
 *
 * The old all-SVG version drew into a fixed 420px viewBox centred in the
 * panel: dead margins either side, names sheared to "Google Ch…", and the
 * animateMotion dots stranded mid-canvas under reduced motion. HTML chips
 * ellipsize properly and carry the real ToolIcon; the middle SVG uses
 * preserveAspectRatio="none" + non-scaling strokes so the curves stretch
 * to whatever width the panel actually has.
 */
export function FocusSankey({ t, pairs, onTool }) {
  const colors = toolColors(t);
  const list = (pairs || []).slice(0, 5);
  const [hot, setHot] = React.useState(null);
  /* The rail svg is stretched horizontally (preserveAspectRatio "none"), so a
     circle would smear into a wide ellipse. Measure the rendered width and
     counter-scale the dots' rx so they stay visually round. */
  const railRef = React.useRef(null);
  const [railW, setRailW] = React.useState(600);
  React.useEffect(() => {
    const el = railRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width;
      if (w) setRailW(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  if (!list.length) return null;
  const left = [];
  const right = [];
  list.forEach((p) => {
    if (!left.includes(p.from)) left.push(p.from);
    if (!right.includes(p.to)) right.push(p.to);
  });
  const PITCH = 46;
  const CHIP_H = 30;
  const rows = Math.max(left.length, right.length);
  const H = rows * PITCH;
  const yAt = (i) => i * PITCH + PITCH / 2;
  const linked = (name) => hot == null
    || list[hot]?.from === name
    || list[hot]?.to === name;
  const reduce = typeof window !== "undefined"
    && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const chip = (name, side, i) => (
    <button
      key={`${side}${name}`}
      type="button"
      className="tgw-a"
      onMouseEnter={() => setHot(list.findIndex((p) => (side === "l" ? p.from : p.to) === name))}
      onClick={side === "r" && onTool ? () => onTool({ name }) : undefined}
      title={name}
      style={{
        display: "flex", alignItems: "center", gap: 8, height: CHIP_H,
        padding: "0 12px 0 6px", borderRadius: 999, border: 0,
        background: linked(name) ? t.c.primarySoft : t.c.surfaceMuted,
        opacity: linked(name) ? 1 : 0.35,
        transition: "opacity .2s ease, background .2s ease",
        cursor: side === "r" && onTool ? "pointer" : "default",
        fontFamily: "inherit", color: "inherit", maxWidth: 200,
        animationDelay: `${0.08 + i * 0.06}s`,
      }}
    >
      <ToolIcon name={name} size={20} />
      <span style={{
        fontSize: 12.5, fontWeight: 650, color: t.c.text, minWidth: 0,
        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
      }}>{name}</span>
    </button>
  );

  return (
    <div
      style={{ display: "flex", alignItems: "flex-start", gap: 10, width: "100%" }}
      onMouseLeave={() => setHot(null)}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: PITCH - CHIP_H, padding: `${(PITCH - CHIP_H) / 2}px 0`, alignItems: "flex-start", flexShrink: 0 }}>
        {left.map((n, i) => chip(n, "l", i))}
      </div>
      <svg
        ref={railRef}
        viewBox={`0 0 100 ${H}`}
        preserveAspectRatio="none"
        style={{ flex: 1, minWidth: 0, height: H, display: "block" }}
        aria-hidden="true"
      >
        <defs>
          {list.map((p, i) => (
            <linearGradient key={`g${i}`} id={`tgwFg${i}`} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor={colors[i % colors.length]} stopOpacity="0.15" />
              <stop offset="45%" stopColor={colors[i % colors.length]} stopOpacity="0.85" />
              <stop offset="100%" stopColor={colors[i % colors.length]} stopOpacity="0.2" />
            </linearGradient>
          ))}
        </defs>
        {list.map((p, i) => {
          const y0 = yAt(left.indexOf(p.from));
          const y1 = yAt(right.indexOf(p.to));
          const on = hot == null || hot === i;
          const w = Math.max(5, (p.count || 8) / 5.5);
          return (
            <g
              key={`${p.from}-${p.to}`}
              onMouseEnter={() => setHot(i)}
              onClick={() => onTool?.({ name: p.to })}
              style={{ cursor: onTool ? "pointer" : "default" }}
            >
              <path
                id={`tgwFlow${i}`}
                className="tgw-flow"
                d={`M0 ${y0} C 42 ${y0}, 58 ${y1}, 100 ${y1}`}
                fill="none"
                stroke={`url(#tgwFg${i})`}
                strokeWidth={hot === i ? w + 3 : w}
                strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
                pathLength="1"
                opacity={on ? 1 : 0.12}
                style={{ animationDelay: `${0.12 + i * 0.14}s`, transition: "opacity .2s ease" }}
              />
              {/* Invisible fat twin so thin rails are still easy to hover. */}
              <path
                d={`M0 ${y0} C 42 ${y0}, 58 ${y1}, 100 ${y1}`}
                fill="none" stroke="transparent" strokeWidth={16}
                vectorEffect="non-scaling-stroke"
              >
                <title>{`${p.from} → ${p.to} · ${p.count}×`}</title>
              </path>
              {/* Traveling dots. opacity starts at 0 and flips on only when the
                  run begins — the old version left dots stranded at the origin
                  during their begin-delay. rx counter-scales the stretched
                  canvas so the dots stay round. */}
              {!reduce && on && [0, 0.45].map((begin) => {
                const rPx = hot === i ? 4.2 : 3.2;
                return (
                  <ellipse
                    key={begin}
                    rx={(rPx * 100) / Math.max(railW, 1)}
                    ry={rPx}
                    fill={colors[i % colors.length]}
                    opacity="0"
                  >
                    <animate
                      attributeName="opacity"
                      to="0.9"
                      dur="0.01s"
                      begin={`${0.85 + i * 0.12 + begin}s`}
                      fill="freeze"
                    />
                    <animateMotion
                      dur={`${2.2 + i * 0.25}s`}
                      repeatCount="indefinite"
                      begin={`${0.85 + i * 0.12 + begin}s`}
                    >
                      <mpath href={`#tgwFlow${i}`} />
                    </animateMotion>
                  </ellipse>
                );
              })}
            </g>
          );
        })}
      </svg>
      <div style={{ display: "flex", flexDirection: "column", gap: PITCH - CHIP_H, padding: `${(PITCH - CHIP_H) / 2}px 0`, alignItems: "flex-start", flexShrink: 0 }}>
        {right.map((n, i) => chip(n, "r", i))}
      </div>
    </div>
  );
}

export function SparkArea({ t, values, color }) {
  const vals = values || [];
  if (vals.length < 2) return null;
  const W = 240, H = 40;
  const mx = Math.max(...vals, 1);
  const pts = vals.map((v, i) => [i * (W / (vals.length - 1)), H - 5 - (v / mx) * (H - 10)]);
  const id = `tgwSp${color.replace(/[^a-z0-9]/gi, "")}`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={40}>
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity=".32" /><stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={smooth(pts, H)} fill={`url(#${id})`} />
      <path d={smooth(pts)} fill="none" stroke={color} strokeWidth="2.1" />
    </svg>
  );
}

export function Tip({ children, title }) {
  return title ? <Tooltip title={title}>{children}</Tooltip> : children;
}
